/**
 * @file BlogFeedOptionsGroups.tsx
 * @description Вид ленты блога (phase19.16 Р2; uiux-design-r2 §8; SB70;
 * db-design-r2 §5) двумя группами-карточками SB122 (uiux-settings-groups
 * §5, шаг 6): «Раскладка» — карточки-схемы [Сетка 2][Сетка 3][Список]
 * (`OptionCards` + `FeedLayoutScheme`, замечание пользователя 2026-10-12
 * «Нужна визуализация»), «Обложки статей»,
 * статей на странице [6][9][12][18]; «Карточка статьи» — рубрика, автор,
 * дата, время чтения, короткое описание. Значения — часть BLOG черновика
 * (`options`), на сайте — после публикации вида. Прежде — одна карточка
 * `BlogFeedOptionsCard`.
 */

import { SITE_BLOG_PER_PAGE, type SiteBlogOptions } from "@chaiberi/shared";

import { FieldHint, SettingsGroup } from "../../ui/settings-groups";
import { CheckRow } from "../CheckRow";
import { GroupField } from "../GroupField";
import { OptionCards } from "../OptionCards";
import { Segmented } from "../Segmented";
import { FeedLayoutScheme } from "./BlogSchemes";

interface Props {
  options: SiteBlogOptions;
  onChange: (options: SiteBlogOptions) => void;
}

const LAYOUT = [
  { value: "grid2" as const, label: "Сетка 2" },
  { value: "grid3" as const, label: "Сетка 3" },
  { value: "list" as const, label: "Список" },
];

const CARD_FIELDS: ReadonlyArray<{ key: keyof SiteBlogOptions["cardFields"]; label: string }> = [
  { key: "category", label: "Рубрика" },
  { key: "author", label: "Автор" },
  { key: "date", label: "Дата" },
  { key: "readTime", label: "Время чтения" },
  { key: "excerpt", label: "Короткое описание" },
];

export function BlogFeedLayoutGroup({ options, onChange }: Props) {
  const layout = LAYOUT.find((l) => l.value === options.feedLayout)?.label ?? "";
  const summary = `${layout} · ${options.feedCovers ? "с обложками" : "без обложек"} · по ${options.perPage}`;
  return (
    <SettingsGroup id="layout" title="Раскладка" hint="Как стоят статьи в ленте и сколько их на странице." summary={summary}>
      <GroupField label="Раскладка">
        <OptionCards
          aria-label="Раскладка ленты"
          columns={2}
          value={options.feedLayout}
          onChange={(feedLayout) => onChange({ ...options, feedLayout })}
          options={LAYOUT.map((l) => ({ ...l, preview: <FeedLayoutScheme layout={l.value} /> }))}
        />
      </GroupField>
      <CheckRow checked={options.feedCovers} onChange={(feedCovers) => onChange({ ...options, feedCovers })}>
        Обложки статей
      </CheckRow>
      <GroupField label="Статей на странице">
        <Segmented
          aria-label="Статей на странице"
          options={SITE_BLOG_PER_PAGE.map((n) => ({ value: String(n), label: String(n) }))}
          value={String(options.perPage)}
          onChange={(v) => onChange({ ...options, perPage: Number(v) as SiteBlogOptions["perPage"] })}
        />
      </GroupField>
    </SettingsGroup>
  );
}

export function BlogFeedCardGroup({ options, onChange }: Props) {
  const shown = CARD_FIELDS.filter((f) => options.cardFields[f.key]).map((f) => f.label.toLowerCase());
  return (
    <SettingsGroup id="card" title="Карточка статьи" tocLabel="Карточка" hint="Что видно в карточке статьи, кроме названия." summary={shown.length > 0 ? shown.join(", ") : "только название"}>
      <div className="flex flex-wrap gap-x-4">
        {CARD_FIELDS.map((f) => (
          <CheckRow key={f.key} checked={options.cardFields[f.key]} onChange={(on) => onChange({ ...options, cardFields: { ...options.cardFields, [f.key]: on } })}>
            {f.label}
          </CheckRow>
        ))}
      </div>
      {shown.length === 0 ? <FieldHint className="mt-0">Всё снято — в карточке останется название статьи{options.feedCovers ? " с обложкой" : ""}.</FieldHint> : null}
    </SettingsGroup>
  );
}
